import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { postsService, WordPressPost } from "../../api/api.service";
import "./SideBar.css";

const Sidebar: React.FC = () => {
  const [recentPosts, setRecentPosts] = useState<WordPressPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("search") || "";
  });

  // Sync search term with URL changes (e.g. back/forward navigation)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlSearchTerm = searchParams.get("search") || "";
    setSearchTerm(urlSearchTerm);
  }, [location.search]);

  // Still keeping keywords static for now as requested, or until we have tag logic
  const popularKeywords = [
    { name: "Business", count: 1 },
    { name: "Corporate", count: 1 },
    { name: "Criminal", count: 2 },
    { name: "Insurance", count: 2 },
    { name: "International", count: 2 },
    { name: "Tax Law", count: 1 },
  ];

  useEffect(() => {
    const fetchRecentPosts = async () => {
      try {
        setLoading(true);
        // Fetch 3 recent posts
        const data = await postsService.getPosts({ per_page: 3 });
        setRecentPosts(data);
      } catch (error) {
        console.error("Failed to fetch sidebar posts", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentPosts();
  }, []);

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/blog?search=${encodeURIComponent(searchTerm.trim())}`);
    } else {
      // If search is empty, go to main blog page (show all)
      navigate('/blog');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <aside className="sidebar">

      {/* Search Box - Moved to top */}
      <div className="sidebar-box search-box">
        <input
          type="text"
          placeholder="Search ..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyPress}
        />
        <svg
          className="search-icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          onClick={handleSearch}
          style={{ cursor: 'pointer' }}
        >
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
      </div>




      {/* Popular Keywords */}
      <div className="sidebar-box">
        <h3 className="sidebar-title">Popular Keyword</h3>
        <ul className="keyword-list">
          {popularKeywords.map((keyword, index) => (
            <li key={index} className="keyword-item">
              <span className="keyword-name">{keyword.name}</span>
              <span className="keyword-count">({keyword.count})</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Recent Posts */}
      <div className="sidebar-box">
        <h3 className="sidebar-title">Recent Posts</h3>
        <div className="recent-posts-list">
          {loading ? (
            <div style={{ padding: '10px', color: '#666' }}>Loading...</div>
          ) : (
            recentPosts.map((post) => {
              const featureMedia = post._embedded?.["wp:featuredmedia"]?.[0];
              const imageUrl = postsService.getMediaBaseUrl(featureMedia);

              return (
                <Link key={post.id} to={`/blog/${post.id}`} className="recent-post-item">
                  <img src={imageUrl} alt={post.title.rendered} className="recent-post-thumb" />
                  <div className="recent-post-info">
                    <div className="recent-post-date">{postsService.formatDate(post.date)}</div>
                    <div className="recent-post-title" dangerouslySetInnerHTML={{ __html: post.title.rendered }} />
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </div>


    </aside>
  );
};

export default Sidebar;
